import {
  Accordion,
  Alert,
  Button,
  Paper,
  Skeleton,
  Stack,
  Text
} from "@mantine/core";
import type { DatesRangeValue } from "@mantine/dates";
import { MonthPickerInput } from "@mantine/dates";
import { IconZoomExclamation } from "@tabler/icons-react";
import { DateTime } from "luxon";
import { useEffect } from "react";
import { useVariantSites } from "../../../../hooks/useVariantSites";
import { AlertPresets } from "../../../../mantine/mantinePresets";
import { LoadOrError } from "../../../LoadOrError/LoadOrError";
import { SearchBar } from "../../../SearchBar/SearchBar";
import { EARLIEST_YEAR } from "../../common";
import { useEnergyDataAvailabilityPageState } from "../hooks/useEnergyDataAvailabilityPageState";
import { SiteAvailability } from "./components/SiteAvailability";
import "./EnergyDataAvailability.scss";

const MIN_DATE_RANGE = new Date(EARLIEST_YEAR, 0, 1);
const MAX_DATE_RANGE = DateTime.now().endOf("month").toJSDate();

interface EnergyDataAvailabilityProps {
  variantId: number;
}

function EnergyDataAvailability({ variantId }: EnergyDataAvailabilityProps) {
  const { pageState, setPageState } = useEnergyDataAvailabilityPageState();

  const {
    sites,
    isLoading: isSitesLoading,
    error: sitesError
  } = useVariantSites(variantId);

  useEffect(() => {
    if (
      sites.length === 1 &&
      pageState.selectedSiteId !== sites[0].id.toString()
    ) {
      setPageState((oldPageState) => ({
        ...oldPageState,
        selectedSiteId: sites[0].id.toString()
      }));
    }
  }, [setPageState, sites, pageState.selectedSiteId]);

  function handleDatePickerChange(dateRange: DatesRangeValue<string>) {
    // only react if a valid & complete range is set
    if (dateRange[0] && dateRange[1]) {
      const firstDate = DateTime.fromISO(dateRange[0]);
      const lastDate = DateTime.fromISO(dateRange[1]).endOf("month");
      if (firstDate.isValid && lastDate.isValid) {
        setPageState((state) => {
          return {
            ...state,
            firstDate,
            lastDate
          };
        });
      }
    }
  }

  function handleSearchChange(newValue: string) {
    setPageState({
      ...pageState,
      searchTerm: newValue
    });
  }

  function handleAccordionChange(selectedPanel: string | null) {
    setPageState({
      ...pageState,
      selectedSiteId: selectedPanel
    });
  }

  const sitesFiltered = sites?.filter(
    (site) =>
      site?.name &&
      site.name
        .toLowerCase()
        .includes(pageState.searchTerm?.toLowerCase() || "")
  );

  if (!isSitesLoading && !sitesError && sites.length === 0) {
    return (
      <Alert
        color={AlertPresets.warning.color}
        icon={AlertPresets.warning.icon}
      >
        Es sind noch keine Liegenschaften konfiguriert.
      </Alert>
    );
  }

  if (isSitesLoading) {
    return (
      <>
        <Stack>
          <Skeleton h={36} />
          <Skeleton h={74} />
          <Skeleton h={36} />
          <div>
            <Paper>
              <Skeleton h={26} />
            </Paper>
            <Paper>
              <Skeleton h={26} />
            </Paper>
            <Paper>
              <Skeleton h={26} />
            </Paper>
          </div>
        </Stack>
      </>
    );
  }

  return (
    <LoadOrError error={sitesError}>
      <div className="EnergyDataAvailability">
        <Stack>
          <MonthPickerInput
            allowSingleDateInRange
            className="date-range-input"
            defaultValue={[
              pageState.firstDate.toJSDate(),
              pageState.lastDate.toJSDate()
            ]}
            label="Zeitraum"
            maxDate={MAX_DATE_RANGE}
            minDate={MIN_DATE_RANGE}
            type="range"
            onChange={handleDatePickerChange}
          />
          <Alert color={AlertPresets.info.color} icon={AlertPresets.info.icon}>
            In diesem Abschnitt sehen Sie die Energiedaten, die von Ihnen,
            Untermesssystemen oder Messstellenbetreibern bereitgestellt wurden.
            Aufgrund der täglichen Marktkommunikation können sich die Daten an
            den Lokationen kontinuierlich ändern. Bitte beachten Sie, dass
            Zeiträume in der Zukunft nicht auswählbar sind.
          </Alert>
          <SearchBar
            btnHidden
            controlledValue={pageState.searchTerm}
            placeholder="Suche nach Liegenschaften"
            onChange={(event) => handleSearchChange(event.currentTarget.value)}
            onClear={() => handleSearchChange("")}
          />
          {sitesFiltered && sitesFiltered.length > 0 ? (
            <Accordion
              value={pageState.selectedSiteId}
              onChange={handleAccordionChange}
            >
              {sitesFiltered.map((site) => (
                <SiteAvailability
                  highlight={pageState.searchTerm}
                  key={site.id}
                  selectedDateRange={[pageState.firstDate, pageState.lastDate]}
                  siteId={site.id}
                  siteName={site.name}
                />
              ))}
            </Accordion>
          ) : (
            <Stack align="center">
              <IconZoomExclamation size={40} stroke={1} />
              <Text>
                Für &quot;{pageState.searchTerm}&quot; konnten wir leider keine
                Liegenschaften finden. Versuchen Sie einen anderen Suchbegriff.
              </Text>
              <Button
                color="primary"
                variant="transparent"
                onClick={() => handleSearchChange("")}
              >
                Suche zurücksetzen
              </Button>
            </Stack>
          )}
        </Stack>
      </div>
    </LoadOrError>
  );
}

export { EnergyDataAvailability, EnergyDataAvailabilityProps };
